import { cva } from 'class-variance-authority';
import { endOfDay, format, isSameDay, parseISO, startOfDay } from 'date-fns';

import { useCalendar } from '@/components/calendar/contexts/calendar-context';
import {
  getCalendarEventColor,
  pastelEventColors,
} from '@/components/calendar/event-colors';
import { isTimeBlockEvent } from '@/components/calendar/helpers';
import { zonedEvent } from '@/lib/timezone';

import { EventDetailsDialog } from '@/components/calendar/components/dialogs/event-details-dialog';
import { DraggableEvent } from '@/components/calendar/components/dnd/draggable-event';

import { cn } from '@/lib/utils';

import type { IEvent } from '@/components/calendar/interfaces';
import type { VariantProps } from 'class-variance-authority';

const eventBadgeVariants = cva(
  'mx-1 flex size-auto h-6.5 select-none items-center justify-between gap-1.5 truncate whitespace-nowrap rounded-md border px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  {
    variants: {
      color: {
        // Colored and mixed variants
        blue: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300 [&_.event-dot]:fill-blue-600',
        green:
          'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300 [&_.event-dot]:fill-green-600',
        red: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 [&_.event-dot]:fill-red-600',
        yellow:
          'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300 [&_.event-dot]:fill-yellow-600',
        purple:
          'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300 [&_.event-dot]:fill-purple-600',
        orange:
          'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300 [&_.event-dot]:fill-orange-600',
        gray: 'border-neutral-200 bg-neutral-50 text-neutral-900 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 [&_.event-dot]:fill-neutral-600',

        // Dot variants
        'blue-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-blue-600',
        'green-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-green-600',
        'red-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-red-600',
        'yellow-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-yellow-600',
        'purple-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-purple-600',
        'orange-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-orange-600',
        'gray-dot':
          'bg-neutral-50 dark:bg-neutral-900 [&_.event-dot]:fill-neutral-600',
      },
      multiDayPosition: {
        first:
          'relative z-10 mr-0 w-[calc(100%_-_3px)] rounded-r-none border-r-0 [&>span]:mr-2.5',
        middle:
          'relative z-10 mx-0 w-[calc(100%_+_1px)] rounded-none border-x-0',
        last: 'ml-0 rounded-l-none border-l-0',
        none: '',
      },
    },
    defaultVariants: {
      color: 'blue-dot',
    },
  }
);

interface IProps
  extends Omit<
    VariantProps<typeof eventBadgeVariants>,
    'color' | 'multiDayPosition'
  > {
  event: IEvent;
  cellDate: Date;
  eventCurrentDay?: number;
  eventTotalDays?: number;
  className?: string;
  position?: 'first' | 'middle' | 'last' | 'none';
}

export function MonthEventBadge({
  event,
  cellDate,
  eventCurrentDay,
  eventTotalDays,
  className,
  position: propPosition,
}: IProps) {
  const { badgeVariant, timeZone, colorBy, config } = useCalendar();

  const itemStart = startOfDay(parseISO(event.startDate));
  const itemEnd = endOfDay(parseISO(event.endDate));

  if (cellDate < itemStart || cellDate > itemEnd) return null;

  let position: 'first' | 'middle' | 'last' | 'none' | undefined;

  if (propPosition) {
    position = propPosition;
  } else if (eventCurrentDay && eventTotalDays) {
    position = 'none';
  } else if (isSameDay(itemStart, itemEnd)) {
    position = 'none';
  } else if (isSameDay(cellDate, itemStart)) {
    position = 'first';
  } else if (isSameDay(cellDate, itemEnd)) {
    position = 'last';
  } else {
    position = 'middle';
  }

  const renderBadgeText = ['first', 'none'].includes(position);

  const isUnavailability = event.metadata?.type === 'unavailability';
  const baseColor = getCalendarEventColor(event, colorBy);

  const color = (
    badgeVariant === 'dot' ? `${baseColor}-dot` : baseColor
  ) as VariantProps<typeof eventBadgeVariants>['color'];

  const eventBadgeClasses = cn(
    eventBadgeVariants({ color, multiDayPosition: position, className }),
    config?.colorByEnabled &&
      !isTimeBlockEvent(event) &&
      badgeVariant !== 'dot' &&
      pastelEventColors[baseColor],
    isUnavailability && 'bg-unavailability-stripes'
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (e.currentTarget instanceof HTMLElement) e.currentTarget.click();
    }
  };

  return (
    <DraggableEvent event={event}>
      <EventDetailsDialog event={event}>
        <div
          role="button"
          tabIndex={0}
          className={eventBadgeClasses}
          onKeyDown={handleKeyDown}
        >
          <div className="flex items-center gap-1.5 truncate">
            {!['middle', 'last'].includes(position) &&
              ['mixed', 'dot'].includes(badgeVariant) && (
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  className="event-dot shrink-0"
                  aria-hidden="true"
                >
                  <circle cx="4" cy="4" r="4" />
                </svg>
              )}

            {renderBadgeText && (
              <p className="flex-1 truncate font-semibold">
                {eventCurrentDay && (
                  <span className="text-xs">
                    Day {eventCurrentDay} of {eventTotalDays} •{' '}
                  </span>
                )}
                {event.title}
              </p>
            )}
          </div>

          {renderBadgeText && (
            <span>
              {format(zonedEvent(event.startDate, timeZone), 'h:mm a')}
            </span>
          )}
        </div>
      </EventDetailsDialog>
    </DraggableEvent>
  );
}
